import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../lib/permissions';
import { useCountry } from '../contexts/CountryContext';
import { Card } from '../components/ui/card';
import { StatStrip } from '../components/StatStrip';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { RefreshCw, Search, AlertTriangle, AlertCircle, Package, CheckCircle, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LoadError } from '../components/LoadError';
import { indiaToday } from '../components/sales/format';
import { useTableControls, SortHeader, TablePager } from '../components/TableControls';
import { REORDER_STATUS, StockStatusBadge, StockLegend, formatDaysOfStock, daysOfStockTitle, daysSortValue } from '../lib/stockStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Card icon colours; labels and thresholds come from the shared REORDER_STATUS.
const STATUS_CONFIG = {
  CRITICAL: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  REORDER_NEEDED: { color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  LOW_STOCK: { color: 'bg-amber-100 text-amber-700', icon: Package },
  HEALTHY: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
};
const STATUS_ORDER = Object.keys(STATUS_CONFIG);
const CHANNEL_LABELS = { BOTH: 'Seller + Retail', SELLER_ONLY: 'Seller only', RETAIL_ONLY: 'Retail only' };

const SEARCH_KEYS = ['sku_code', 'product_name', 'supplier'];
const INITIAL_SORT = { key: 'status', dir: 'asc' };
const SORT_VALUE = {
  status: (r) => STATUS_ORDER.indexOf(r.status),
  days_of_stock: (r) => daysSortValue(r.days_of_stock, r.available_stock, r.daily_velocity),
  order_value: (r) => (r.reorder_qty > 0 ? r.reorder_qty * (r.cost_price || 0) : 0),
};

const csvCell = (value) => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const ReorderPage = () => {
  const { user } = useAuth();
  // Order value is reorder qty x purchase cost: admins only (the API omits cost for others).
  const showCost = isAdmin(user);
  const { country } = useCountry();
  const [reorderData, setReorderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');

  const fetchReorderData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/reorder?country=${country}`, { headers: getAuthHeaders() });
      setReorderData(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => {
    fetchReorderData();
  }, [fetchReorderData]);

  const statusFiltered = useMemo(
    () => (statusFilter === 'ALL' ? reorderData : reorderData.filter(item => item.status === statusFilter)),
    [reorderData, statusFilter],
  );
  const table = useTableControls(statusFiltered, { searchKeys: SEARCH_KEYS, initialSort: INITIAL_SORT, pageSize: 50, sortValue: SORT_VALUE });
  const { setPage, setQuery } = table;
  // /reorder?sku=CODE (from a Business Review action) opens the list searched for that SKU.
  const [searchParams] = useSearchParams();
  const skuParam = searchParams.get('sku') || '';
  useEffect(() => { if (skuParam) setQuery(skuParam); }, [skuParam, setQuery]);
  // A new status filter is a new list: start it on page 1.
  useEffect(() => { setPage(1); }, [statusFilter, setPage]);
  const searchTerm = table.query;

  // Export what is on screen (search + status filter + sort), all pages.
  const handleExport = () => {
    const rows = table.all;
    if (!rows.length) { toast.error('Nothing to export'); return; }
    const head = ['Status', 'SKU Code', 'Product Name', 'Channel', 'Available (units)', 'Daily velocity (units/day)', 'Days of stock', 'Reorder qty (units)', 'MOQ', 'Supplier', ...(showCost ? ['Cost price', 'Order value'] : [])];
    const lines = rows.map(r => [
      REORDER_STATUS[r.status]?.label || r.status,
      r.sku_code,
      r.product_name,
      CHANNEL_LABELS[r.channel_mode] || r.channel_mode,
      r.available_stock,
      r.daily_velocity,
      formatDaysOfStock(r.days_of_stock, { available: r.available_stock, velocity: r.daily_velocity }),
      r.reorder_qty > 0 ? r.reorder_qty : 0,
      r.moq ?? '',
      r.supplier || '',
      ...(showCost ? [r.cost_price ?? '', r.reorder_qty > 0 ? r.reorder_qty * (r.cost_price || 0) : 0] : []),
    ].map(csvCell).join(','));
    const blob = new Blob([`\ufeff${[head.join(','), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `reorder-${country}-${indiaToday()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    toast.success(`Exported ${rows.length} SKUs`);
  };

  const statusCounts = {
    CRITICAL: reorderData.filter(i => i.status === 'CRITICAL').length,
    REORDER_NEEDED: reorderData.filter(i => i.status === 'REORDER_NEEDED').length,
    LOW_STOCK: reorderData.filter(i => i.status === 'LOW_STOCK').length,
    HEALTHY: reorderData.filter(i => i.status === 'HEALTHY').length
  };

  const totalReorderValue = reorderData
    .filter(i => i.reorder_qty > 0)
    .reduce((sum, item) => sum + (item.reorder_qty * (item.cost_price || 0)), 0);

  const totalReorderUnits = reorderData
    .filter(i => i.reorder_qty > 0)
    .reduce((sum, item) => sum + item.reorder_qty, 0);

  return (
    <div className="space-y-6" data-testid="reorder-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Reorder Engine</h1>
          <p className="mt-1 text-[15px] text-gray-500">Smart replenishment recommendations based on sales velocity</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={loading || !!error || table.total === 0}
            className="h-9 px-4 text-[13px] font-medium rounded-lg"
            data-testid="reorder-export-button"
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button
            onClick={fetchReorderData}
            disabled={loading}
            className="h-9 px-4 bg-[#007AFF] hover:bg-[#0066CC] text-white text-[13px] font-medium rounded-lg"
            data-testid="refresh-button"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {error && <LoadError what="reorder recommendations" error={error} onRetry={fetchReorderData} retrying={loading} testId="reorder-error" />}

      {/* Status Summary Cards */}
      {/* Summary: statuses are filters; totals follow, in one compact row */}
      <StatStrip testId="reorder-summary" items={[
        ...Object.keys(STATUS_CONFIG).map(status => ({
          key: status.toLowerCase(), label: REORDER_STATUS[status].label, hint: REORDER_STATUS[status].rule,
          value: loading || error ? '–' : (statusCounts[status] || 0),
          tone: { CRITICAL: 'red', REORDER_NEEDED: 'orange', LOW_STOCK: 'amber', HEALTHY: 'green' }[status] || 'gray',
          active: statusFilter === status, onClick: () => setStatusFilter(statusFilter === status ? 'ALL' : status),
        })),
        { key: 'items', label: 'items to reorder', tone: 'gray', value: loading || error ? '–' : statusCounts.CRITICAL + statusCounts.REORDER_NEEDED },
        { key: 'units', label: 'units to order', tone: 'gray', value: loading || error ? '–' : totalReorderUnits.toLocaleString('en-IN') },
        ...(showCost ? [{ key: 'value', label: 'order value', tone: 'gray', value: loading || error ? '–' : `₹${totalReorderValue.toLocaleString('en-IN')}` }] : []),
      ]} />

      {/* Search */}
      <Card className="p-4 xl:p-5 bg-white rounded-2xl border-0 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <Search className="h-5 w-5 text-gray-500" aria-hidden="true" />
          <Input
            placeholder="Search by SKU, product name, or supplier..."
            aria-label="Search by SKU, product name, or supplier"
            value={searchTerm}
            onChange={(e) => table.setQuery(e.target.value)}
            className="flex-1 min-w-0 max-w-md xl:max-w-lg border-0 bg-gray-50 rounded-lg"
            data-testid="search-input"
          />
          {statusFilter !== 'ALL' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStatusFilter('ALL')}
              className="text-[13px]"
            >
              Clear Filter
            </Button>
          )}
          <StockLegend map={REORDER_STATUS} title="What do these statuses mean?" className="basis-full" testId="reorder-legend" />
        </div>
      </Card>

      {/* Reorder Table */}
      <Card className="bg-white rounded-2xl border-0 shadow-sm overflow-hidden">
        {error ? (
          <div className="text-center py-12 text-gray-600">Reorder data unavailable. Use Retry above.</div>
        ) : loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-gray-600" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading reorder data…
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reorder-table">
              <thead className="bg-gray-50 text-[12px] xl:text-[13px] font-semibold text-gray-600 uppercase tracking-wide">
                <tr>
                  <SortHeader label="Status" sortKey="status" table={table} className="p-4 xl:p-5" />
                  <SortHeader label="SKU Code" sortKey="sku_code" table={table} className="p-4 xl:p-5" />
                  <SortHeader label="Product Name" sortKey="product_name" table={table} className="p-4 xl:p-5" />
                  <SortHeader label="Channel" sortKey="channel_mode" table={table} className="p-4 xl:p-5" />
                  <SortHeader label="Available (units)" sortKey="available_stock" table={table} align="right" className="p-4 xl:p-5" />
                  <SortHeader label="Daily Velocity" sortKey="daily_velocity" table={table} align="right" className="p-4 xl:p-5" />
                  <SortHeader label="Days of Stock" sortKey="days_of_stock" table={table} align="right" className="p-4 xl:p-5" />
                  <SortHeader label="Reorder Qty" sortKey="reorder_qty" table={table} align="right" className="p-4 xl:p-5" />
                  <SortHeader label="Supplier" sortKey="supplier" table={table} className="p-4 xl:p-5" />
                  {showCost && <SortHeader label="Order Value" sortKey="order_value" table={table} align="right" className="p-4 xl:p-5" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {table.pageRows.map((item, index) => {
                  return (
                    <tr key={item.sku_code} className="hover:bg-gray-50 transition-colors" data-testid={`reorder-row-${index}`}>
                      <td className="p-4 xl:p-5">
                        <StockStatusBadge status={item.status} map={REORDER_STATUS} />
                      </td>
                      <td className="p-4 xl:p-5 font-mono text-[13px] xl:text-[14px] font-medium text-black">{item.sku_code}</td>
                      <td className="p-4 xl:p-5 text-[13px] xl:text-[14px] text-gray-700">{item.product_name}</td>
                      <td className="p-4 xl:p-5">
                        <span className="text-[11px] px-2 py-1 rounded bg-gray-100 text-gray-700 whitespace-nowrap">
                          {CHANNEL_LABELS[item.channel_mode] || item.channel_mode}
                        </span>
                      </td>
                      <td className="p-4 xl:p-5 text-right font-mono text-[13px] xl:text-[14px]">
                        <span className={item.available_stock <= 0 ? 'text-red-700 font-semibold' : ''}>
                          {item.available_stock}
                        </span>
                      </td>
                      <td className="p-4 xl:p-5 text-right font-mono text-[13px] xl:text-[14px] text-gray-600">
                        {item.daily_velocity}/day
                      </td>
                      <td className="p-4 xl:p-5 text-right font-mono text-[13px] xl:text-[14px] whitespace-nowrap">
                        <span
                          className={item.available_stock <= 0 ? 'text-red-700 font-semibold' : item.days_of_stock < 14 ? 'text-orange-700 font-medium' : 'text-gray-600'}
                          title={daysOfStockTitle(item.days_of_stock, { available: item.available_stock, velocity: item.daily_velocity })}
                        >
                          {formatDaysOfStock(item.days_of_stock, { available: item.available_stock, velocity: item.daily_velocity })}
                        </span>
                      </td>
                      <td className="p-4 xl:p-5 text-right font-mono text-[14px] xl:text-[15px] font-semibold">
                        <span className={item.reorder_qty > 0 ? 'text-blue-700' : 'text-gray-500'}>
                          {item.reorder_qty > 0 ? item.reorder_qty : '-'}
                        </span>
                      </td>
                      <td className="p-4 xl:p-5 text-[13px] xl:text-[14px] text-gray-600">{item.supplier || '-'}</td>
                      {showCost && (
                      <td className="p-4 xl:p-5 text-right font-mono text-[13px] xl:text-[14px] font-medium">
                        {item.reorder_qty > 0 ? `₹${(item.reorder_qty * item.cost_price).toLocaleString('en-IN')}` : '-'}
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {table.total === 0 && (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || statusFilter !== 'ALL' ? 'No items match your filters' : 'No SKUs found'}
              </div>
            )}
          </div>
          <TablePager table={table} itemLabel="SKUs" className="border-t border-gray-100" />
          </>
        )}
      </Card>
    </div>
  );
};
